import { showApiError, showSuccess } from "@/lib/toast";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addAreaCampaign, addCampaign, addCampaignDetailItem, getCampaignDetails } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { FormSchemaType, CampaignMetaForm } from "./form/CampaignMetaForm";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { ROUTES } from "@/constants/routeConstants";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { BaseFormLayout } from "../shared/BaseFormLayout";
import { CampaignType } from "@/lib/types";
import { formatToUTC } from "@/lib/dateUtils";
import { addDays } from "date-fns";
import _ from "lodash";

export const AddNewCampaignPage = () => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);

  useBreadcrumbs([
    {
      name: t("entities.campaign"),
      path: ROUTES.CAMPAIGN,
    },
    {
      name: t("common.addNewEntity", { entity: t("entities.campaign") }),
      path: ROUTES.CAMPAIGN_ADD,
    },
  ]);

  const { goToCampaignDetail } = useAppNavigate();
  const queryClient = useQueryClient();
  const addCampaignMutation = useMutation({
    mutationFn: async (values: FormSchemaType) => {
      const newCampaignData = await addCampaign({
        name: values.name,
        firmGuid: String(activeFirm?.firmGuid),
        isCombineAndShuffle: values.isCombineAndShuffle,
        defaultContentId: values.contentId || undefined,
        campaignType: values.type as unknown as CampaignType,
      });

      if (!newCampaignData) {
        return Promise.reject();
      }

      const newCampaignGuid = newCampaignData.data.newDataId;
      const { data: campaignDetails } = await getCampaignDetails(newCampaignGuid, String(activeFirm?.firmGuid));
      const newCampaignIntId = campaignDetails.intId;

      if (values.periodStart && values.periodEnd) {
        if (values.periodEnd === values.periodStart) {
          values.periodEnd = addDays(formatToUTC(values.periodEnd), 1).toISOString();
        } else {
          values.periodEnd = formatToUTC(values.periodEnd);
        }

        await addCampaignDetailItem({
          firmId: String(activeFirm?.firmGuid),
          campaignId: newCampaignIntId,
          periodStart: formatToUTC(values.periodStart),
          periodEnd: values.periodEnd,
          isActive: true,
          withCustomWeekDays: false,
        });
      }

      if (values.areas?.length) {
        for (const areaId of _.uniq(values.areas)) {
          await addAreaCampaign({
            firmGuid: String(activeFirm?.firmGuid),
            campaignId: newCampaignIntId,
            areaId,
          });
        }
      }

      return Promise.resolve({ data: { newDataId: newCampaignGuid } });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["getCampaign"],
      });
      showSuccess(t("common.addedSuccess"));
      goToCampaignDetail(data.data.newDataId);
    },
    onError: showApiError,
  });

  return (
    <BaseFormLayout title={t("common.addNewEntity", { entity: t("entities.campaign") })}>
      <CampaignMetaForm
        buttonText={t("common.add")}
        onSubmit={(values) => addCampaignMutation.mutate(values)}
        isLoading={addCampaignMutation.isPending}
        showTypeSelect
      />
    </BaseFormLayout>
  );
};
